import React, { useState, useMemo, useEffect, useCallback } from 'react';
import { useIntl } from 'react-intl';
import { Download } from 'lucide-react';
import { useHabitContext } from '../../context/HabitContext';
import { getDaysInMonth, getMonthShort } from '../../utils/dateUtils';
import { Habit } from '../../types';
import { downloadCsv } from '../../utils/csv';
import SectionHeader from '../common/SectionHeader';
import Spinner from '../common/Spinner';
import './HabitYearDetail.scss';

interface Props {
  year: number;
  headerActions?: React.ReactNode;
}

interface CellTooltipItem {
  key: string;
  name: string;
  color: string;
  value: string;
  done: boolean;
  segmentColor?: string;
  beforeCreated?: boolean;
}

type CellTooltipPlacement = 'center' | 'start' | 'end';

const MONTHS = Array.from({ length: 12 }, (_, i) => i);
const DAYS = Array.from({ length: 31 }, (_, i) => i + 1);
const RATING_FILLS = [0, 22, 40, 58, 78, 100];

const HabitYearDetail: React.FC<Props> = ({ year, headerActions }) => {
  const { categories, isLoading, getHabitEntriesFor } = useHabitContext();
  const { formatMessage: t, locale } = useIntl();

  const [viewYear, setViewYear] = useState(year);

  const allHabits: Habit[] = useMemo(
    () => categories.flatMap(c => c.habits).filter(h => !h.temporary),
    [categories],
  );

  // Multi-select: a Set of selected habit ids
  const [selectedIds, setSelectedIds] = useState<Set<string>>(() => {
    const first = allHabits[0]?.id;
    return first ? new Set([first]) : new Set();
  });

  // When habits load asynchronously (empty on mount), select the first one
  useEffect(() => {
    setSelectedIds(prev => {
      if (prev.size === 0 && allHabits.length > 0) {
        return new Set([allHabits[0].id]);
      }
      return prev;
    });
  }, [allHabits]);

  const toggleHabitSelection = (id: string) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  const toggleAll = () => {
    setSelectedIds(prev => {
      if (prev.size === allHabits.length) return new Set();
      return new Set(allHabits.map(h => h.id));
    });
  };

  const selectedHabits = allHabits.filter(h => selectedIds.has(h.id));
  const isCombined = selectedHabits.length > 1;
  const habit = selectedHabits.length === 1 ? selectedHabits[0] : null;

  const now = new Date();
  const todayYear = now.getFullYear();
  const todayMonth = now.getMonth();
  const todayDay = now.getDate();

  // Use context yearlyData when viewing current year, compute otherwise
  // Returns true for days that predate the habit's creation
  const isBeforeCreatedAt = useCallback((h: Habit, m: number, day: number): boolean => {
    if (!h.createdAt) return false;
    const created = new Date(h.createdAt);
    const cy = created.getFullYear();
    const cm = created.getMonth();
    const cd = created.getDate();
    if (viewYear > cy) return false;
    if (viewYear === cy && m > cm) return false;
    if (viewYear === cy && m === cm && day >= cd) return false;
    return true;
  }, [viewYear]);

  const effectiveYearlyData = useMemo(() => {
    // Returns the effective day range for a habit in a given month.
    // startDay: first valid day (1-indexed). effDays: count of valid days.
    const getEffectiveBounds = (h: Habit, m: number): { effDays: number; startDay: number } => {
      const dim = getDaysInMonth(m, viewYear);
      if (!h.createdAt) return { effDays: dim, startDay: 1 };
      const created = new Date(h.createdAt);
      const cy = created.getFullYear();
      const cm = created.getMonth();
      const cd = created.getDate();
      if (viewYear > cy || (viewYear === cy && m > cm)) return { effDays: dim, startDay: 1 };
      if (viewYear === cy && m === cm) return { effDays: Math.max(0, dim - cd + 1), startDay: cd };
      return { effDays: 0, startDay: 1 }; // entire month is before creation
    };

    const result: Record<string, number[]> = {};
    allHabits.forEach(h => {
      const pcts = MONTHS.map(m => {
        const { effDays, startDay } = getEffectiveBounds(h, m);
        if (effDays === 0) return 0;
        const entries = getHabitEntriesFor(h.id, m, viewYear);
        // Only count entries on or after the creation day to match the denominator
        const validEntries = startDay > 1 ? entries.filter(e => e.day >= startDay) : entries;
        if (h.type === 'checkbox') {
          const done = validEntries.filter(e => e.completed).length;
          return Math.round((done / effDays) * 100);
        }
        const rated = validEntries.filter(e => (e.rating ?? 0) > 0);
        if (rated.length === 0) return 0;
        const avg = rated.reduce((s, e) => s + (e.rating ?? 0), 0) / rated.length;
        return Math.round(avg * 10) / 10;
      });
      result[h.id] = pcts;
    });
    return result;
  }, [viewYear, allHabits, getHabitEntriesFor]);

  const monthlyPcts = habit ? (effectiveYearlyData[habit.id] ?? Array(12).fill(0)) : Array(12).fill(0);
  const ratedMonths = monthlyPcts.filter((p: number) => p > 0);

  const checkboxYearToDatePct = useCallback((h: Habit): number => {
    let done = 0;
    let total = 0;
    MONTHS.forEach((m) => {
      const dim = getDaysInMonth(m, viewYear);
      const entries = getHabitEntriesFor(h.id, m, viewYear);
      for (let d = 1; d <= dim; d++) {
        const isFuture =
          viewYear > todayYear
          || (viewYear === todayYear && m > todayMonth)
          || (viewYear === todayYear && m === todayMonth && d > todayDay);
        if (isFuture || isBeforeCreatedAt(h, m, d)) continue;
        total += 1;
        if (entries.find((e) => e.day === d)?.completed) done += 1;
      }
    });
    if (total === 0) return 0;
    return Math.round((done / total) * 100);
  }, [getHabitEntriesFor, isBeforeCreatedAt, todayDay, todayMonth, todayYear, viewYear]);

  const yearScore = habit
    ? habit.type === 'rating'
      ? (ratedMonths.length === 0 ? 0 : ratedMonths.reduce((sum: number, p: number) => sum + p, 0) / ratedMonths.length)
      : checkboxYearToDatePct(habit)
    : 0;
  const yearPct = habit?.type === 'rating'
    ? Math.round(yearScore * 10) / 10
    : Math.round(yearScore);
  const yearFillPct = habit?.type === 'rating' ? Math.round((yearPct / 5) * 100) : yearPct;
  const yearLabel = habit?.type === 'rating'
    ? (yearPct > 0 ? yearPct.toFixed(1) : '0')
    : `${yearPct}%`;
  const formatMonthStat = (value: number) => {
    if (value <= 0) return '';
    return habit?.type === 'rating' ? value.toFixed(1) : String(value);
  };

  // Pre-load all entries by month (for single-habit view)
  const allEntries = useMemo(
    () => habit
      ? Object.fromEntries(MONTHS.map(m => [m, getHabitEntriesFor(habit.id, m, viewYear)]))
      : {} as Record<number, ReturnType<typeof getHabitEntriesFor>>,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [habit?.id, viewYear],
  );

  // Pre-load entries for selected habits (for combined multi-habit view)
  const selectedHabitEntries = useMemo(
    () => isCombined
      ? Object.fromEntries(
          selectedHabits.map(h => [
            h.id,
            Object.fromEntries(MONTHS.map(m => [m, getHabitEntriesFor(h.id, m, viewYear)])),
          ]),
        )
      : {},
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [isCombined, selectedHabits.map(h => h.id).join(','), viewYear],
  );

  const isFutureCell = (m: number, day: number) =>
    viewYear > todayYear ||
    (viewYear === todayYear && m > todayMonth) ||
    (viewYear === todayYear && m === todayMonth && day > todayDay);

  const getCellDateLabel = (m: number, day: number) => `${getMonthShort(m, locale)} ${day}, ${viewYear}`;

  const getTooltipPlacement = (index: number, total: number): CellTooltipPlacement => {
    const edgeThreshold = total > 20 ? 2 : 1;

    if (index < edgeThreshold) return 'start';
    if (index >= total - edgeThreshold) return 'end';
    return 'center';
  };

  const renderCellTooltip = (
    m: number,
    day: number,
    items: CellTooltipItem[],
    placement: CellTooltipPlacement = 'center',
  ) => {
    if (items.length === 0) return null;

    return (
      <div className={`hyd__cell-tooltip hyd__cell-tooltip--${placement}`} role="tooltip">
        <div className="hyd__cell-tooltip-date">{getCellDateLabel(m, day)}</div>
        <div className="hyd__cell-tooltip-items">
          {items.map(item => (
            <div
              key={item.key}
              className={`hyd__cell-tooltip-item${item.done ? ' hyd__cell-tooltip-item--done' : ''}`}
            >
              <span
                className="hyd__cell-tooltip-dot"
                style={{ background: item.color }}
              />
              <span className="hyd__cell-tooltip-name">{item.name}</span>
              <span className="hyd__cell-tooltip-value">{item.value}</span>
            </div>
          ))}
        </div>
      </div>
    );
  };

  // For combined mode: build one fixed segment per selected habit plus tooltip data.
  const getCombinedCellItems = (m: number, day: number): CellTooltipItem[] => {
    const daysInMonth = getDaysInMonth(m, viewYear);
    if (day > daysInMonth) return [];

    if (isFutureCell(m, day)) return [];

    return selectedHabits.map(h => {
      if (isBeforeCreatedAt(h, m, day)) {
        return {
          key: h.id,
          name: h.name,
          color: h.color,
          value: '',
          done: false,
          beforeCreated: true,
        };
      }

      const entry = selectedHabitEntries[h.id]?.[m]?.find((e: any) => e.day === day);
      if (h.type === 'checkbox') {
        const done = Boolean(entry?.completed);
        return {
          key: h.id,
          name: h.name,
          color: h.color,
          value: done ? t({ id: 'habits.done' }) : t({ id: 'habits.notDone' }),
          done,
          segmentColor: done ? h.color : 'var(--border-medium)',
        };
      }

      const rating = entry?.rating ?? 0;
      return {
        key: h.id,
        name: h.name,
        color: h.color,
        value: rating > 0 ? `${rating}/5` : t({ id: 'habits.noRating' }),
        done: rating > 0,
        segmentColor: rating > 0 ? h.color : 'var(--border-medium)',
      };
    });
  };

  const renderCombinedSegments = (items: CellTooltipItem[]) => {
    const segments = items.filter(item => !item.beforeCreated);
    return (
      <div className="hyd__cell-segments">
        {segments.map(item => (
          <div
            key={item.key}
            className={`hyd__cell-segment${item.done ? '' : ' hyd__cell-segment--empty'}`}
            style={item.done ? { background: item.segmentColor } : undefined}
          />
        ))}
      </div>
    );
  };

  const getSingleCellTooltipItems = (m: number, day: number): CellTooltipItem[] => {
    const daysInMonth = getDaysInMonth(m, viewYear);
    if (day > daysInMonth || !habit || isFutureCell(m, day) || isBeforeCreatedAt(habit, m, day)) return [];

    const entry = allEntries[m]?.find(e => e.day === day);
    if (habit.type === 'checkbox') {
      const done = Boolean(entry?.completed);
      return [{
        key: habit.id,
        name: habit.name,
        color: habit.color,
        value: done ? t({ id: 'habits.done' }) : t({ id: 'habits.notDone' }),
        done,
      }];
    }

    const rating = entry?.rating ?? 0;
    return [{
      key: habit.id,
      name: habit.name,
      color: habit.color,
      value: rating > 0 ? `${rating}/5` : t({ id: 'habits.noRating' }),
      done: rating > 0,
    }];
  };

  // Overall completion % for combined mode
  const combinedYearPct = useMemo(() => {
    if (!isCombined || selectedHabits.length === 0) return 0;
    const totals = selectedHabits.map(h => {
      const pcts = effectiveYearlyData[h.id] ?? Array(12).fill(0);
      const avg = h.type === 'rating'
        ? (() => {
            const rated = pcts.filter((p: number) => p > 0);
            return rated.length === 0 ? 0 : rated.reduce((s: number, p: number) => s + p, 0) / rated.length;
          })()
        : checkboxYearToDatePct(h);
      return h.type === 'rating' ? Math.round((avg / 5) * 100) : Math.round(avg);
    });
    return Math.round(totals.reduce((s, v) => s + v, 0) / totals.length);
  }, [checkboxYearToDatePct, isCombined, selectedHabits, effectiveYearlyData]);

  const checkboxHabits = selectedHabits.filter((h) => h.type === 'checkbox');
  const ratingHabits = selectedHabits.filter((h) => h.type === 'rating');
  const showDualProgress = isCombined && checkboxHabits.length > 0 && ratingHabits.length > 0;
  const ratingsOnly = isCombined && ratingHabits.length > 0 && checkboxHabits.length === 0;

  const combinedTickPct = useMemo(() => {
    if (checkboxHabits.length === 0) return 0;
    const totals = checkboxHabits.map((h) => checkboxYearToDatePct(h));
    return Math.round(totals.reduce((s, v) => s + v, 0) / totals.length);
  }, [checkboxHabits, checkboxYearToDatePct]);

  const combinedRatingAvg = useMemo(() => {
    if (ratingHabits.length === 0) return 0;
    const totals = ratingHabits.map((h) => {
      const pcts = (effectiveYearlyData[h.id] ?? Array(12).fill(0)).filter((p: number) => p > 0);
      if (pcts.length === 0) return 0;
      return pcts.reduce((s: number, p: number) => s + p, 0) / pcts.length;
    });
    const present = totals.filter((v) => v > 0);
    if (present.length === 0) return 0;
    return Math.round((present.reduce((s, v) => s + v, 0) / present.length) * 10) / 10;
  }, [effectiveYearlyData, ratingHabits]);

  const getCellVisual = (m: number, day: number): {
    className: string;
    colorStyle: React.CSSProperties;
    ratingScore: number | null;
  } => {
    const daysInMonth = getDaysInMonth(m, viewYear);
    if (day > daysInMonth) {
      return { className: 'hyd__cell hyd__cell--void', colorStyle: {}, ratingScore: null };
    }

    if (isFutureCell(m, day) || (habit && isBeforeCreatedAt(habit, m, day))) {
      return {
        className: 'hyd__cell hyd__cell--muted',
        colorStyle: { background: 'var(--bg-secondary)', opacity: 0.35 },
        ratingScore: null,
      };
    }

    const entry = allEntries[m]?.find(e => e.day === day);

    if (habit?.type === 'rating') {
      const rating = Math.min(5, Math.max(0, entry?.rating ?? 0));
      const fill = RATING_FILLS[rating] ?? 0;
      const className = [
        'hyd__cell',
        'hyd__cell--rating',
        rating === 0 ? 'hyd__cell--empty' : '',
        rating >= 4 ? 'hyd__cell--bright' : '',
        rating > 0 && rating < 4 ? `hyd__cell--r${rating}` : '',
      ].filter(Boolean).join(' ');
      return {
        className,
        colorStyle: {
          '--hyd-habit-color': habit.color,
          '--hyd-fill': `${fill}%`,
        } as React.CSSProperties,
        ratingScore: rating,
      };
    }

    let colorStyle: React.CSSProperties = { background: 'var(--bg-secondary)' };
    if (habit?.type === 'checkbox' && entry?.completed) {
      colorStyle = { background: habit.color };
    }
    return { className: 'hyd__cell', colorStyle, ratingScore: null };
  };

  const renderSingleHabitCell = (
    key: React.Key,
    m: number,
    day: number,
    tooltipIndex: number,
    tooltipTotal: number,
  ) => {
    const { className, colorStyle, ratingScore } = getCellVisual(m, day);
    const tooltipItems = getSingleCellTooltipItems(m, day);
    const isVoid = className.includes('hyd__cell--void');
    return (
      <td
        key={key}
        className={className}
        aria-label={tooltipItems.length > 0 ? `${getCellDateLabel(m, day)}: ${tooltipItems[0].name} ${tooltipItems[0].value}` : undefined}
      >
        {!isVoid && (
          <div
            className={`hyd__cell-color${ratingScore !== null ? ' hyd__cell-color--rating' : ''}`}
            style={colorStyle}
          >
            {ratingScore != null && ratingScore > 0 ? ratingScore : ''}
          </div>
        )}
        {tooltipItems.length > 0 && renderCellTooltip(m, day, tooltipItems, getTooltipPlacement(tooltipIndex, tooltipTotal))}
      </td>
    );
  };

  const exportYearCsv = () => {
    const habitsToExport = selectedHabits.length > 0 ? selectedHabits : allHabits;
    const headers = ['date', 'habit', 'type', 'value'];
    const rows: (string | number)[][] = [];
    habitsToExport.forEach((h) => {
      MONTHS.forEach((m) => {
        const dim = getDaysInMonth(m, viewYear);
        const entries = getHabitEntriesFor(h.id, m, viewYear);
        for (let d = 1; d <= dim; d += 1) {
          const entry = entries.find(e => e.day === d);
          const value = h.type === 'checkbox' ? (entry?.completed ? 1 : 0) : (entry?.rating ?? '');
          rows.push([
            `${viewYear}-${String(m + 1).padStart(2, '0')}-${String(d).padStart(2, '0')}`,
            h.name,
            h.type,
            value,
          ]);
        }
      });
    });
    downloadCsv(`habits-${viewYear}.csv`, headers, rows);
  };

  const headerRight = (
    <>
      {headerActions}
      {allHabits.length > 0 && (
        <button
          type="button"
          className="yearly-export-btn"
          onClick={exportYearCsv}
          aria-label={t({ id: 'stats.export.csv' })}
        >
          <Download size={16} />
        </button>
      )}
    </>
  );

  if (isLoading) {
    return (
      <div className="hyd">
        <SectionHeader
          title={String(viewYear)}
          onPrev={() => setViewYear(y => y - 1)}
          onNext={() => setViewYear(y => y + 1)}
          prevLabel={t({ id: 'stats.habits.previousYear' })}
          nextLabel={t({ id: 'stats.habits.nextYear' })}
          actions={headerRight}
        />
        <Spinner block />
      </div>
    );
  }

  if (allHabits.length === 0) {
    return (
      <div className="hyd">
        <SectionHeader
          title={String(viewYear)}
          onPrev={() => setViewYear(y => y - 1)}
          onNext={() => setViewYear(y => y + 1)}
          prevLabel={t({ id: 'stats.habits.previousYear' })}
          nextLabel={t({ id: 'stats.habits.nextYear' })}
          actions={headerRight}
        />
        <div className="hyd-empty">{t({ id: 'habits.noHabits' })}</div>
      </div>
    );
  }

  return (
    <div className="hyd">
      <SectionHeader
        title={String(viewYear)}
        onPrev={() => setViewYear(y => y - 1)}
        onNext={() => setViewYear(y => y + 1)}
        prevLabel={t({ id: 'stats.habits.previousYear' })}
        nextLabel={t({ id: 'stats.habits.nextYear' })}
        actions={headerRight}
      />
      <div className="hyd__selector">
        <button
          className={`hyd__habit-btn hyd__habit-btn--toggle-all${selectedIds.size === allHabits.length ? ' hyd__habit-btn--active' : ''}`}
          onClick={toggleAll}
        >
          {selectedIds.size === allHabits.length ? t({ id: 'habits.deselectAll' }) : t({ id: 'habits.selectAll' })}
        </button>
        {allHabits.map(h => (
          <button
            key={h.id}
            className={`hyd__habit-btn${selectedIds.has(h.id) ? ' hyd__habit-btn--active' : ''}`}
            style={{ '--hyd-color': h.color } as React.CSSProperties}
            onClick={() => toggleHabitSelection(h.id)}
          >
            <span className="hyd__habit-dot" />
            {h.name}
          </button>
        ))}
      </div>

      {/* ── Combined multi-habit grid ── */}
      {selectedHabits.length === 0 && (
        <div className="hyd__empty">{t({ id: 'habits.selectPrompt' })}</div>
      )}

      {isCombined && (
        <div className="hyd__content" style={{ '--hyd-color': ratingsOnly ? (ratingHabits[0]?.color ?? 'var(--text-primary)') : 'var(--text-primary)', '--hyd-year-pct': `${ratingsOnly ? Math.round((combinedRatingAvg / 5) * 100) : combinedYearPct}%` } as React.CSSProperties}>
          <div className="hyd__scroll">
            <table className="hyd__table hyd__table--desktop">
              <thead>
                <tr>
                  <th className="hyd__corner" />
                  {DAYS.map(day => (
                    <th key={day} className="hyd__day-head">{day}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {MONTHS.map(m => (
                  <tr key={m} className="hyd__month-row">
                    <td className="hyd__month-label">{getMonthShort(m, locale)}</td>
                    {DAYS.map(day => {
                      const daysInMonth = getDaysInMonth(m, viewYear);
                      if (day > daysInMonth) {
                        return <td key={day} className="hyd__cell hyd__cell--void" />;
                      }
                      const isFuture = isFutureCell(m, day);
                      if (isFuture) {
                        return (
                          <td key={day} className="hyd__cell">
                            <div className="hyd__cell-color" style={{ background: 'var(--bg-secondary)', opacity: 0.35 }} />
                          </td>
                        );
                      }
                      const items = getCombinedCellItems(m, day);
                      const tooltipItems = items.filter(i => !i.beforeCreated);
                      return (
                        <td
                          key={day}
                          className={`hyd__cell${tooltipItems.length === 0 ? ' hyd__cell--muted' : ''}`}
                          aria-label={tooltipItems.length > 0 ? `${getCellDateLabel(m, day)}: ${tooltipItems.map(item => `${item.name} ${item.value}`).join(', ')}` : undefined}
                        >
                          {renderCombinedSegments(items)}
                          {tooltipItems.length > 0 && renderCellTooltip(m, day, tooltipItems, getTooltipPlacement(day - 1, DAYS.length))}
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>

            <table className="hyd__table hyd__table--mobile">
              <thead>
                <tr>
                  <th className="hyd__corner" />
                  {MONTHS.map(m => (
                    <th key={m} className="hyd__month-head">{getMonthShort(m, locale)}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {DAYS.map(day => (
                  <tr key={day} className="hyd__day-row">
                    <td className="hyd__day-num">{day}</td>
                    {MONTHS.map(m => {
                      const daysInMonth = getDaysInMonth(m, viewYear);
                      if (day > daysInMonth) {
                        return <td key={m} className="hyd__cell hyd__cell--void" />;
                      }
                      const isFuture = isFutureCell(m, day);
                      if (isFuture) {
                        return (
                          <td key={m} className="hyd__cell">
                            <div className="hyd__cell-color" style={{ background: 'var(--bg-secondary)', opacity: 0.35 }} />
                          </td>
                        );
                      }
                      const items = getCombinedCellItems(m, day);
                      const tooltipItems = items.filter(i => !i.beforeCreated);
                      return (
                        <td
                          key={m}
                          className={`hyd__cell${tooltipItems.length === 0 ? ' hyd__cell--muted' : ''}`}
                          aria-label={tooltipItems.length > 0 ? `${getCellDateLabel(m, day)}: ${tooltipItems.map(item => `${item.name} ${item.value}`).join(', ')}` : undefined}
                        >
                          {renderCombinedSegments(items)}
                          {tooltipItems.length > 0 && renderCellTooltip(m, day, tooltipItems, getTooltipPlacement(m, MONTHS.length))}
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {showDualProgress ? (
            <>
              <div
                className="hyd__progress-row"
                style={{ '--hyd-color': checkboxHabits[0].color, '--hyd-year-pct': `${combinedTickPct}%` } as React.CSSProperties}
              >
                <div className="hyd__progress-bar">
                  <div className="hyd__progress-fill" />
                </div>
                <span className="hyd__progress-pct">{combinedTickPct}%</span>
              </div>
              <div
                className="hyd__progress-row"
                style={{ '--hyd-color': ratingHabits[0].color, '--hyd-year-pct': `${Math.round((combinedRatingAvg / 5) * 100)}%` } as React.CSSProperties}
              >
                <div className="hyd__progress-bar">
                  <div className="hyd__progress-fill" />
                </div>
                <span className="hyd__progress-pct">{combinedRatingAvg > 0 ? combinedRatingAvg.toFixed(1) : '0'}</span>
              </div>
            </>
          ) : (
            <div
              className="hyd__progress-row"
              style={ratingsOnly ? { '--hyd-color': ratingHabits[0]?.color, '--hyd-year-pct': `${Math.round((combinedRatingAvg / 5) * 100)}%` } as React.CSSProperties : undefined}
            >
              <div className="hyd__progress-bar">
                <div className="hyd__progress-fill" />
              </div>
              <span className="hyd__progress-pct">
                {ratingsOnly
                  ? (combinedRatingAvg > 0 ? combinedRatingAvg.toFixed(1) : '0')
                  : `${combinedYearPct}%`}
              </span>
            </div>
          )}
        </div>
      )}

      {/* ── Single-habit view ── */}
      {!isCombined && habit && (
        <div className="hyd__content" style={{ '--hyd-color': habit.color, '--hyd-year-pct': `${yearFillPct}%` } as React.CSSProperties}>
          <div className="hyd__scroll">

            {/* ── Desktop: months as rows, days 1-31 as columns ── */}
            <table className="hyd__table hyd__table--desktop">
              <thead>
                <tr>
                  <th className="hyd__corner" />
                  {DAYS.map(day => (
                    <th key={day} className="hyd__day-head">{day}</th>
                  ))}
                  <th className="hyd__avg-head">{t({ id: 'habits.avg' })}</th>
                </tr>
              </thead>
              <tbody>
                {MONTHS.map(m => (
                  <tr key={m} className="hyd__month-row">
                    <td className="hyd__month-label">{getMonthShort(m, locale)}</td>
                    {DAYS.map(day => renderSingleHabitCell(day, m, day, day - 1, DAYS.length))}
                    <td className="hyd__avg-cell">
                      {formatMonthStat(monthlyPcts[m])}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            {/* ── Mobile: days as rows, months as columns ── */}
            <table className="hyd__table hyd__table--mobile">
              <thead>
                <tr>
                  <th className="hyd__corner" />
                  {MONTHS.map(m => (
                    <th key={m} className="hyd__month-head">{getMonthShort(m, locale)}</th>
                  ))}
                </tr>
                <tr className="hyd__avg-row">
                  <th className="hyd__avg-label">{habit.type === 'rating' ? '' : '%'}</th>
                  {MONTHS.map(m => (
                    <th key={m} className="hyd__avg-cell">
                      {formatMonthStat(monthlyPcts[m])}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {DAYS.map(day => (
                  <tr key={day} className="hyd__day-row">
                    <td className="hyd__day-num">{day}</td>
                    {MONTHS.map(m => renderSingleHabitCell(m, m, day, m, MONTHS.length))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="hyd__progress-row">
              <div className="hyd__progress-bar">
                <div className="hyd__progress-fill" />
              </div>
              <span className="hyd__progress-pct">{yearLabel}</span>
            </div>
        </div>
      )}
    </div>
  );
};

export default HabitYearDetail;

